import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/atoms/Badge';
import LicenseStatusBadge, { getLicenseStatus } from '@/components/atoms/LicenseStatusBadge';
import { Spinner } from '@/components/atoms/Spinner';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import { type Contract, useGetClientContracts } from '@/domain/licenses';
import { formatDayDisplay } from '@/utils/date';

import styles from './ClientContractsPanel.module.scss';

type ContractLicense = Contract['licenses'][number];

interface ClientContractsPanelProps {
    clientId: number;
}

/** Ids of the license types with a license running in a contract that is still in force. */
function runningLicenseIds(contracts: Contract[]): Set<number> {
    const running = new Set<number>();
    for (const contract of contracts) {
        if (contract.archivedAt) continue;
        for (const license of contract.licenses) {
            if (getLicenseStatus(license.dayFrom, license.dayTo) === 'active') {
                running.add(license.license.id);
            }
        }
    }
    return running;
}

/** The contracts and licenses behind a client's row, read when the row is opened. */
export default function ClientContractsPanel({ clientId }: ClientContractsPanelProps) {
    const { t } = useTranslation();
    const { data, isLoading, error } = useGetClientContracts(clientId);

    if (isLoading) return <Spinner size="sm" />;
    if (error) return <p role="alert">{t('internal.clients.contractsLoadError')}</p>;

    const contracts = [...(data ?? [])].sort(
        (a, b) => Number(Boolean(a.archivedAt)) - Number(Boolean(b.archivedAt)) || a.id - b.id
    );
    if (contracts.length === 0) return <p>{t('internal.clients.noContracts')}</p>;

    const running = runningLicenseIds(contracts);
    const columns: DataTableColumnDef<ContractLicense>[] = [
        {
            id: 'name',
            header: t('common.labels.name'),
            cell: ({ row }) => row.original.license.name,
        },
        {
            id: 'from',
            header: t('common.labels.from'),
            cell: ({ row }) => formatDayDisplay(row.original.dayFrom),
        },
        {
            id: 'to',
            header: t('molecules.common.to'),
            cell: ({ row }) => formatDayDisplay(row.original.dayTo),
        },
        {
            id: 'status',
            header: t('common.labels.status'),
            cell: ({ row }) => {
                const { dayFrom, dayTo, license } = row.original;
                const status = getLicenseStatus(dayFrom, dayTo);
                // An ended license beside a running one of the same type is only history.
                return status === 'expired' && running.has(license.id) ? (
                    <Badge tone="neutral" size="md">
                        {t('internal.clients.superseded')}
                    </Badge>
                ) : (
                    <LicenseStatusBadge status={status} />
                );
            },
        },
    ];

    return (
        <div className={styles.panel}>
            {contracts.map((contract) => (
                <section key={contract.id} className={styles.contract}>
                    <h3 className={styles.heading}>
                        {contract.description || t('internal.clients.untitledContract')}
                        <span className={styles.meta}>
                            {t('internal.clients.contractedBioscopes', { count: contract.count })}
                        </span>
                        {contract.archivedAt && (
                            <Badge tone="neutral" size="xs">
                                {t('internal.clients.status.archived')}
                            </Badge>
                        )}
                    </h3>
                    <DataTable
                        data={contract.licenses}
                        columns={columns}
                        getRowId={(license) => String(license.id)}
                        enableSorting={false}
                        emptyState={t('internal.clients.noLicenses')}
                    />
                </section>
            ))}
        </div>
    );
}
